import React from 'react';
import { connect } from 'react-redux';

import {
    Checkbox,
    FormHelp,
    Label,
    LabeledControl,
    Radio,
    Spacer,
    Text,
} from '@zillow/constellation';
import { Col } from '@zillow/zrs-comet';

import NotificationConstants from 'app/shared/constants/NotificationConstants';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import PostingActions from 'app/shared/actions/PostingActions';
import AdditionalAmenities from 'app/shared/components/postingPath/AdditionalAmenities';
import { StyledAnchor } from '../UtilsStyledComponents';
import { StyledRow } from './StyledComponents';

const SAVING = 'Saving...';
const laundry = {
    shared: 'Shared',
    inUnit: 'In Unit',
    none: 'None',
};

class AmenitiesAndRules extends React.Component {
    constructor(props) {
        super(props);

        this.setLaundryType = this.setLaundryType.bind(this);
        this.updatePets = this.updatePets.bind(this);
    }

    setLaundryType(e, type) {
        this.props.updateStatusMessage(SAVING);

        const dataToSave = {
            fieldName: 'laundry',
            value: type,
            propertyId: this.props.propertyId,
        };

        return this.props.dispatch(PostingActions.property.edit(dataToSave))
            .then(() => {
                const laundryData = { laundry: type };
                this.props.updateFormData(laundryData);
                this.props.updateStatusMessage('All data saved!', NotificationConstants.SUCCESS);
            });
    }

    updatePets(newFormData) {
        let shouldDispatchData = true;

        this.props.updateStatusMessage(SAVING);

        const formDataPetsAllowed = this.props.formData.petsAllowed;
        const { cats, smallDogs, largeDogs } = formDataPetsAllowed;
        let catsUpdated = cats;
        let smallDogsUpdated = smallDogs;
        let largeDogsUpdated = largeDogs;
        switch (newFormData) {
            case 'noPets':
                catsUpdated = false;
                smallDogsUpdated = false;
                largeDogsUpdated = false;
                break;
            case 'smallDogs':
                smallDogsUpdated = !smallDogs;
                break;
            case 'largeDogs':
                largeDogsUpdated = !largeDogs;
                break;
            case 'cats':
                catsUpdated = !cats;
                break;
            default:
                shouldDispatchData = false;
                break;
        }
        const none = !catsUpdated && !smallDogsUpdated && !largeDogsUpdated;
        const petDataWithPropertyId = {
            cats: catsUpdated,
            smallDogs: smallDogsUpdated,
            largeDogs: largeDogsUpdated,
            propertyId: this.props.propertyId,
        };

        const updatedPetsAllowedFormData = {
            petsAllowed: {
                cats: catsUpdated,
                smallDogs: smallDogsUpdated,
                largeDogs: largeDogsUpdated,
                none,
            },
        };
        this.props.updateFormData(updatedPetsAllowedFormData);

        if (shouldDispatchData) {
            this.props.dispatch(PostingActions.property.pets(petDataWithPropertyId))
                .then(() => {
                    this.props.updateStatusMessage('All data saved!', NotificationConstants.SUCCESS);
                });
        }
    }

    render() {
        const {
            errorObj,
            formData,
            updateStatusMessage,
            propertyId,
            updateFormData,
        } = this.props;
        const { amenities, petsAllowed } = formData;
        const {
            cats,
            smallDogs,
            largeDogs,
            none,
        } = petsAllowed;
        const { petsAllowed: petsAllowedErrorObj } = errorObj;
        const {
            cats: catsError,
            smallDogs: smallDogsError,
            largeDogs: largeDogsError,
            none: noneError,
        } = petsAllowedErrorObj;
        const displayPetsAllowedError = catsError
            || smallDogsError
            || largeDogsError
            || noneError;
        const isDisabled = this.props.isInputFormDisabled || false;

        return (
            <div className="AmenitiesAndRules">
                <Text
                    marginBottom="xs"
                    mediaQuery={{
                        lg: {
                            marginBottom: 'sm',
                        },
                    }}
                    fontType="h4"
                    as="div"
                >
                    Amenities and rules
                </Text>

                <StyledAnchor />

                <Label>Amenities</Label>
                <LabeledControl
                    size="md"
                    controlId="A/C"
                    label={<Label>A/C</Label>}
                    control={(
                        <Checkbox
                            checked={this.props.formData.airConditioning === true}
                            onChange={() => this.props.immediateUpdateAndSave(
                                'airConditioning',
                                !formData.airConditioning,
                                {
                                    componentGroupName: AnalyticsConstants.LISTING_COMPONENT_GROUP_NAME
                                        .AMENITIES,
                                }
                            )}
                        />
                    )}
                    disabled={isDisabled}
                />
                <LabeledControl
                    size="md"
                    controlId="BalconyOrDeck"
                    label={<Label>Balcony or deck</Label>}
                    control={(
                        <Checkbox
                            checked={this.props.formData.balconyOrDeck === true}
                            onChange={() => this.props.immediateUpdateAndSave(
                                'balconyOrDeck',
                                !formData.balconyOrDeck,
                                {
                                    componentGroupName: AnalyticsConstants.LISTING_COMPONENT_GROUP_NAME
                                        .AMENITIES,
                                }
                            )}
                        />
                    )}
                    disabled={isDisabled}
                />
                <LabeledControl
                    size="md"
                    controlId="Furnished"
                    label={<Label>Furnished</Label>}
                    control={(
                        <Checkbox
                            checked={this.props.formData.furnished === true}
                            onChange={() => this.props.immediateUpdateAndSave(
                                'furnished',
                                !formData.furnished,
                                {
                                    componentGroupName: AnalyticsConstants.LISTING_COMPONENT_GROUP_NAME
                                        .AMENITIES,
                                }
                            )}
                        />
                    )}
                    disabled={isDisabled}
                />
                <LabeledControl
                    size="md"
                    controlId="HardwoodFloor"
                    label={<Label>Hardwood floor</Label>}
                    control={(
                        <Checkbox
                            checked={this.props.formData.hardwoodFloors === true}
                            onChange={() => this.props.immediateUpdateAndSave(
                                'hardwoodFloors',
                                !formData.hardwoodFloors,
                                {
                                    componentGroupName: AnalyticsConstants.LISTING_COMPONENT_GROUP_NAME
                                        .AMENITIES,
                                }
                            )}
                        />
                    )}
                    disabled={isDisabled}
                />
                <LabeledControl
                    size="md"
                    controlId="WheelchairAccess"
                    label={<Label>Wheelchair access</Label>}
                    control={(
                        <Checkbox
                            checked={this.props.formData.wheelchairAccess === true}
                            onChange={() => this.props.immediateUpdateAndSave(
                                'wheelchairAccess',
                                !formData.wheelchairAccess,
                                {
                                    componentGroupName: AnalyticsConstants.LISTING_COMPONENT_GROUP_NAME
                                        .AMENITIES,
                                }
                            )}
                        />
                    )}
                    disabled={isDisabled}
                />
                <LabeledControl
                    size="md"
                    controlId="GarageParking"
                    label={<Label>Garage parking</Label>}
                    control={(
                        <Checkbox
                            checked={this.props.formData.garageParking === true}
                            onChange={() => this.props.immediateUpdateAndSave(
                                'garageParking',
                                !formData.garageParking,
                                {
                                    componentGroupName: AnalyticsConstants.LISTING_COMPONENT_GROUP_NAME
                                        .AMENITIES,
                                }
                            )}
                        />
                    )}
                    disabled={isDisabled}
                />
                <LabeledControl
                    size="md"
                    controlId="Off-streetParking"
                    label={<Label>Off-street parking</Label>}
                    control={(
                        <Checkbox
                            checked={this.props.formData.offStreetParking === true}
                            onChange={() => this.props.immediateUpdateAndSave(
                                'offStreetParking',
                                !formData.offStreetParking,
                                {
                                    componentGroupName: AnalyticsConstants.LISTING_COMPONENT_GROUP_NAME
                                        .AMENITIES,
                                }
                            )}
                        />
                    )}
                    disabled={isDisabled}
                />
                <Spacer marginY="xs" />
                <StyledRow>
                    <Col xs={12} md={7}>
                        <AdditionalAmenities
                            label="Additional Amenities"
                            updateStatusMessage={updateStatusMessage}
                            previouslySavedAmenities={amenities}
                            updateFormData={updateFormData}
                            propertyId={propertyId}
                        />
                    </Col>
                </StyledRow>
                <Spacer marginY="xs" />
                <Label>Laundry</Label>
                <LabeledControl
                    controlId="None"
                    label={<Label>None</Label>}
                    control={(
                        <Radio
                            name="LabeledControl-Laundry"
                            checked={this.props.formData.laundry === laundry.none}
                            onChange={e => this.setLaundryType(e, laundry.none)}
                            data-test="property-amenities-laundry-none"
                        />
                    )}
                    disabled={isDisabled}
                />
                <LabeledControl
                    controlId="InUnit"
                    label={<Label>In unit</Label>}
                    control={(
                        <Radio
                            name="LabeledControl-Laundry"
                            checked={this.props.formData.laundry === laundry.inUnit}
                            onChange={e => this.setLaundryType(e, laundry.inUnit)}
                            data-test="property-amenities-laundry-in-unit"
                        />
                    )}
                    disabled={isDisabled}
                />
                <LabeledControl
                    controlId="SharedOrIn-building"
                    label={<Label>Shared or in-building</Label>}
                    control={(
                        <Radio
                            name="LabeledControl-Laundry"
                            checked={this.props.formData.laundry === laundry.shared}
                            onChange={e => this.setLaundryType(e, laundry.shared)}
                            data-test="property-amenities-laundry-in-building"
                        />
                    )}
                    disabled={isDisabled}
                />
                {errorObj.laundry && (
                    <FormHelp error={true}>{errorObj.laundry}</FormHelp>
                )}
                <Spacer marginY="xs" />
                <Label>Pets</Label>
                <LabeledControl
                    size="md"
                    controlId="NoPetsAllowed"
                    label={<Label>No pets allowed</Label>}
                    control={(
                        <Checkbox
                            checked={none}
                            onChange={() => this.updatePets('noPets')}
                        />
                    )}
                    disabled={isDisabled}
                />
                <LabeledControl
                    size="md"
                    controlId="CatsOK"
                    label={<Label>Cats OK</Label>}
                    control={(
                        <Checkbox
                            checked={cats}
                            onChange={() => this.updatePets('cats')}
                        />
                    )}
                    disabled={isDisabled}
                />
                <LabeledControl
                    size="md"
                    controlId="SmallDogsOK"
                    label={<Label>Small dogs OK</Label>}
                    control={(
                        <Checkbox
                            checked={smallDogs}
                            onChange={() => this.updatePets('smallDogs')}
                        />
                    )}
                    disabled={isDisabled}
                />
                <LabeledControl
                    size="md"
                    controlId="LargeDogsOK"
                    label={<Label>Large dogs OK</Label>}
                    control={(
                        <Checkbox
                            checked={largeDogs}
                            onChange={() => this.updatePets('largeDogs')}
                        />
                    )}
                    disabled={isDisabled}
                />
                {displayPetsAllowedError ?
                    (
                        <FormHelp error={true}>{displayPetsAllowedError}</FormHelp>
                    // eslint-disable-next-line indent
                    ) : null
                }
                <Spacer marginY="md" />
            </div>
        );
    }
}

export default connect()(AmenitiesAndRules);
